/**
 * AI Agent Panel Component
 * Integrates AI agents with assistant management
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Psychology as AIIcon,
  BugReport as DebugIcon,
  Analytics as AnalyticsIcon,
  Speed as OptimizeIcon,
  Architecture as ArchitectureIcon,
  Search as SEOIcon,
  Brush as UXIcon,
  PlayArrow as ExecuteIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useAIAgents, AI_AGENTS, AGENT_ACTIONS } from '../hooks/useAIAgents';

interface AIAgentPanelProps {
  assistantId?: string;
  projectId?: string;
  assistantConfig?: any;
}

const AIAgentPanel: React.FC<AIAgentPanelProps> = ({
  assistantId,
  projectId,
  assistantConfig
}) => {
  const {
    loading,
    error,
    agents,
    executeAgent,
    analyzeAssistant,
    debugAssistant,
    improveAssistant,
    generateInsights,
    predictOutcomes,
    listAgents,
    checkHealth
  } = useAIAgents();

  const [selectedTab, setSelectedTab] = useState(0);
  const [results, setResults] = useState<any>({});
  const [serverHealthy, setServerHealthy] = useState<boolean | null>(null);
  const [preflight, setPreflight] = useState<{ db?: boolean; system?: any } | null>(null);

  useEffect(() => {
    // Load agents and check health on mount
    listAgents();
    checkHealth().then(setServerHealthy);
    // Preflight checks
    (async () => {
      try {
        const db = await fetch('/api/health/db', { credentials: 'include' }).then(r=>r.json()).catch(()=>({ ok:false }));
        const sys = await fetch('/api/health/system', { credentials: 'include' }).then(r=>r.json()).catch(()=>null);
        setPreflight({ db: !!db?.ok, system: sys });
      } catch {}
    })();
  }, []);

  const handleAnalyze = async () => {
    if (!assistantId) return;
    const analysis = await analyzeAssistant(assistantId);
    setResults((prev: any) => ({ ...prev, analysis }));
  };

  const handleDebug = async () => {
    if (!assistantId) return;
    const debugInfo = await debugAssistant(assistantId);
    setResults((prev: any) => ({ ...prev, debugInfo }));
  };

  const handleImprove = async () => {
    if (!assistantConfig) return;
    const improvements = await improveAssistant(assistantConfig);
    setResults((prev: any) => ({ ...prev, improvements }));
  };

  const handleInsights = async () => {
    if (!assistantId) return;
    const insights = await generateInsights(assistantId);
    setResults((prev: any) => ({ ...prev, insights }));
  };

  const handlePredict = async () => {
    if (!projectId) return;
    const predictions = await predictOutcomes(projectId);
    setResults((prev: any) => ({ ...prev, predictions }));
  };

  const executeCustomAgent = async (agentId: string, action: string) => {
    const response = await executeAgent({
      agentId,
      action: action as any,
      parameters: {
        assistantId,
        projectId,
        timeRange: 'last_7_days'
      }
    });
    
    if (response) {
      setResults((prev: any) => ({
        ...prev,
        [`${agentId}_${action}`]: response
      }));
    }
  };

  const getAgentIcon = (agentId: string) => {
    switch (agentId) {
      case AI_AGENTS.CODE_REVIEW:
        return <AnalyticsIcon />;
      case AI_AGENTS.DEBUGGER:
        return <DebugIcon />;
      case AI_AGENTS.MINDMAP:
        return <ArchitectureIcon />;
      case AI_AGENTS.SEO:
        return <SEOIcon />;
      case AI_AGENTS.UX:
        return <UXIcon />;
      default:
        return <AIIcon />;
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5" component="h2">
            <AIIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            AI Agent Assistant
          </Typography>
          
          <Box display="flex" alignItems="center" gap={1}>
            {serverHealthy !== null && (
              <Chip
                icon={serverHealthy ? <SuccessIcon /> : <ErrorIcon />}
                label={serverHealthy ? 'MCP Connected' : 'MCP Offline'}
                color={serverHealthy ? 'success' : 'error'}
                size="small"
              />
            )}
            <IconButton onClick={() => checkHealth().then(setServerHealthy)} size="small">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Tabs value={selectedTab} onChange={(_, v) => setSelectedTab(v)} sx={{ mb: 2 }}>
          <Tab label="Quick Actions" />
          <Tab label="AI Agents" />
          <Tab label="Results" />
          <Tab label="Preflight" />
        </Tabs>

        {selectedTab === 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 48%' } }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AnalyticsIcon />}
                onClick={handleAnalyze}
                disabled={loading || !assistantId}
              >
                Analyze Performance
              </Button>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 48%' } }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<DebugIcon />}
                onClick={handleDebug}
                disabled={loading || !assistantId}
              >
                Debug Issues
              </Button>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 48%' } }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<OptimizeIcon />}
                onClick={handleImprove}
                disabled={loading || !assistantConfig}
              >
                Generate Improvements
              </Button>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 48%' } }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ArchitectureIcon />}
                onClick={handleInsights}
                disabled={loading || !assistantId}
              >
                Business Insights
              </Button>
            </Box>
            {projectId && (
              <Box sx={{ flex: '1 1 100%' }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AIIcon />}
                  onClick={handlePredict}
                  disabled={loading}
                >
                  Predict Project Outcomes
                </Button>
              </Box>
            )}
          </Box>
        )}

        {selectedTab === 1 && (
          <List>
            {agents.map((agent) => (
              <React.Fragment key={agent.id}>
                <ListItem>
                  <ListItemIcon>
                    {getAgentIcon(agent.id)}
                  </ListItemIcon>
                  <ListItemText
                    primary={agent.name}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {agent.description}
                        </Typography>
                        <Box display="flex" gap={1} mt={1}>
                          <Tooltip title="Analyze">
                            <IconButton
                              size="small"
                              onClick={() => executeCustomAgent(agent.id, AGENT_ACTIONS.ANALYZE)}
                              disabled={loading}
                            >
                              <AnalyticsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Optimize">
                            <IconButton
                              size="small"
                              onClick={() => executeCustomAgent(agent.id, AGENT_ACTIONS.OPTIMIZE)}
                              disabled={loading}
                            >
                              <OptimizeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Generate">
                            <IconButton
                              size="small"
                              onClick={() => executeCustomAgent(agent.id, AGENT_ACTIONS.GENERATE)}
                              disabled={loading}
                            >
                              <ExecuteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                          {agent.tools.map(tool => (
                            <Chip key={tool} label={tool} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}

        {selectedTab === 2 && (
          <Box>
            {Object.keys(results).length === 0 ? (
              <Typography color="text.secondary" align="center">
                No results yet. Execute some agents to see results here.
              </Typography>
            ) : (
              Object.entries(results).map(([key, value]) => (
                <Box key={key} mb={2}>
                  <Typography variant="h6" gutterBottom>
                    {key.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: 'grey.100',
                      p: 2,
                      borderRadius: 1,
                      maxHeight: 300,
                      overflow: 'auto'
                    }}
                  >
                    <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        )}

        {selectedTab === 3 && (
          <Box>
            {preflight ? (
              <>
                <Alert severity={preflight.db ? 'success' : 'error'} sx={{ mb: 2 }}>
                  Database: {preflight.db ? 'Connected' : 'Unavailable'}
                </Alert>
                {preflight.system ? (
                  <List>
                    <ListItem><ListItemText primary={`Uptime: ${preflight.system.uptimeSec}s`} /></ListItem>
                    <ListItem><ListItemText primary={`CPU Cores: ${preflight.system.cpuCount}`} /></ListItem>
                    <ListItem><ListItemText primary={`Node: ${preflight.system.node}`} /></ListItem>
                  </List>
                ) : (
                  <Alert severity="warning">System info unavailable</Alert>
                )}
              </>
            ) : (
              <LinearProgress />
            )}
          </Box>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AIAgentPanel;